import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";

export const usePersonalConnections = (knownUserId?: string) => {
  const { user } = useAuth();
  const [isPersonallyKnown, setIsPersonallyKnown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  // Check if user is personally known
  useEffect(() => {
    if (user && knownUserId) {
      checkPersonalConnection();
    }
  }, [user, knownUserId]);

  const checkPersonalConnection = async () => {
    if (!user || !knownUserId) return;

    setChecking(true);
    try {
      const { data, error } = await supabase
        .from('user_personal_connections')
        .select('id')
        .eq('user_id', user.id)
        .eq('known_user_id', knownUserId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setIsPersonallyKnown(!!data);
    } catch (error) {
      console.error('Error checking personal connection:', error);
    } finally {
      setChecking(false);
    }
  };

  const togglePersonalConnection = async () => {
    if (!user || !knownUserId) return false;

    setLoading(true);
    try {
      if (isPersonallyKnown) {
        // Remove connection
        const { error } = await supabase
          .from('user_personal_connections')
          .delete()
          .eq('user_id', user.id)
          .eq('known_user_id', knownUserId);

        if (error) throw error;

        setIsPersonallyKnown(false);
        toast({
          title: "Connection Removed",
          description: "User removed from personally known contacts.",
        });
      } else {
        // Add connection
        const { error } = await supabase
          .from('user_personal_connections')
          .insert({
            user_id: user.id,
            known_user_id: knownUserId
          });

        if (error) throw error;

        setIsPersonallyKnown(true);
        toast({
          title: "Connection Added",
          description: "User marked as personally known.",
        });
      }
      return true;
    } catch (error) {
      console.error('Error toggling personal connection:', error);
      toast({
        title: "Error",
        description: "Failed to update personal connection.",
        variant: "destructive"
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    isPersonallyKnown,
    loading,
    checking,
    togglePersonalConnection
  };
};